import React from 'react';
import { Chart } from 'primereact/chart';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { Section } from '@/components/Section';
import { ProjectService } from '@/api/project';
import { TestIssueService } from '@/api/testIssue';
import { Loading } from '@/components/Loading';
import { useNavigate, useParams } from 'react-router-dom';
import { FaShare } from 'react-icons/fa';
import { ShareIssueWeeklyChart } from './ShareIssueWeeklyChart';
import { useProtectedRoute } from '@/routes/ProtectedRouteContext';
import { useKeycloak } from '@react-keycloak/web';

export type IWeeklyStatus = {
  labels: string[];
  datasets: {
    label: string;
    backgroundColor: string[];
    data: number[];
  }[];
};

// Generate a limited color palette
const generateColors = (count: number) => {
  const colors = [
    '#36A2EB',
    '#FF6384',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#FF9F40',
  ];
  return colors.slice(0, count);
};

// Helper function to calculate total
const calculateTotal = (data: number[]) =>
  data.reduce((sum: number, value: number) => sum + value, 0);

export const ShareTestIssue: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPublic } = useProtectedRoute();
  const { keycloak } = useKeycloak();

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);
  const { data, isFetching } = TestIssueService.useGetShare(id);

  // Chart options
  const chartOptions = {
    plugins: {
      legend: { position: 'bottom' },
    },
  };

  // Generate dynamic colors
  const statusColors = generateColors(Object.keys(data?.status || {}).length);
  const priorityColors = generateColors(
    Object.keys(data?.priority || {}).length,
  );
  const severityColors = generateColors(
    Object.keys(data?.severity || {}).length,
  );
  const assignedUserColors = generateColors(
    Object.keys(data?.assigned_user || {}).length,
  );

  // Chart data
  const createChartData = (
    labels: string[],
    data: number[],
    colors: string[],
  ) => ({
    labels,
    datasets: [{ data, backgroundColor: colors, hoverBackgroundColor: colors }],
  });

  const statusData = createChartData(
    Object.keys(data?.status || {}),
    Object.values(data?.status || []),
    statusColors,
  );
  const priorityData = createChartData(
    Object.keys(data?.priority || {}),
    Object.values(data?.priority || []),
    priorityColors,
  );
  const severityData = createChartData(
    Object.keys(data?.severity || {}),
    Object.values(data?.severity || []),
    severityColors,
  );
  const assignedUsersData = createChartData(
    Object.keys(data?.assigned_user || {}),
    Object.values(data?.assigned_user || []),
    assignedUserColors,
  );

  const weeklyStatusData: IWeeklyStatus = {
    labels: data?.weekly_status.map(item => item.tanggal) || [],
    datasets: [
      {
        label: 'Total',
        backgroundColor: generateColors(data?.weekly_status.length ?? 0),
        data: data?.weekly_status.map(item => item.total) || [],
      },
    ],
  };

  const totalStatus = calculateTotal(Object.values(data?.status ?? {}));
  const totalPriority = calculateTotal(Object.values(data?.priority ?? {}));
  const totalSeverity = calculateTotal(Object.values(data?.severity ?? {}));
  const totalAssignedUsers = calculateTotal(
    Object.values(data?.assigned_user ?? {}),
  );

  if (isFetching || isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <div className="tab-group">
      <Section
        title={`Test Issue - ${projectData?.title}`}
        actions={
          !isPublic ? (
            <OutlineButton
              icon={<FaShare />}
              onClick={() =>
                window.open(`/public/shareable/test-issue/${id}`, '_blank')
              }
            >
              Shareable Issue View
            </OutlineButton>
          ) : undefined
        }
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <ShareIssueWeeklyChart weeklyStatusData={weeklyStatusData} />

          <div className="w-1/2 card">
            <h5>Status (Total: {totalStatus})</h5>
            <Chart type="doughnut" data={statusData} options={chartOptions} />
            <div className="mt-4">
              <ul className="p-0 m-0 list-none">
                {Object.entries(data?.status || {}).map(
                  ([key, value], index) => (
                    <li key={key} className="flex items-center mb-2">
                      <span
                        className="block w-3 h-3 mr-2"
                        style={{ backgroundColor: statusColors[index] }}
                      ></span>
                      <span>{`${value} ${key}`}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>

          <div className="w-1/2 card">
            <h5>Priority (Total: {totalPriority})</h5>
            <Chart type="doughnut" data={priorityData} options={chartOptions} />
            <div className="mt-4">
              <ul className="p-0 m-0 list-none">
                {Object.entries(data?.priority || {}).map(
                  ([key, value], index) => (
                    <li key={key} className="flex items-center mb-2">
                      <span
                        className="block w-3 h-3 mr-2"
                        style={{ backgroundColor: priorityColors[index] }}
                      ></span>
                      <span>{`${value} ${key}`}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>

          <div className="w-1/2 card">
            <h5>Severity (Total: {totalSeverity})</h5>
            <Chart type="doughnut" data={severityData} options={chartOptions} />
            <div className="mt-4">
              <ul className="p-0 m-0 list-none">
                {Object.entries(data?.severity || {}).map(
                  ([key, value], index) => (
                    <li key={key} className="flex items-center mb-2">
                      <span
                        className="block w-3 h-3 mr-2"
                        style={{ backgroundColor: severityColors[index] }}
                      ></span>
                      <span>{`${value} ${key}`}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>

          <div className="w-1/2 card">
            <h5>Assigned Users (Total: {totalAssignedUsers})</h5>
            <Chart
              type="doughnut"
              data={assignedUsersData}
              options={chartOptions}
            />
            <div className="mt-4">
              <ul className="p-0 m-0 list-none">
                {Object.entries(data?.assigned_user || {}).map(
                  ([key, value], index) => (
                    <li key={key} className="flex items-center mb-2">
                      <span
                        className="block w-3 h-3 mr-2"
                        style={{ backgroundColor: assignedUserColors[index] }}
                      ></span>
                      <span>{`${value} ${key}`}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {keycloak.authenticated && (
        <div className="tab-list">
          <OutlineButton onClick={() => navigate(`/project/${id}/test-issue`)}>
            Back
          </OutlineButton>
        </div>
      )}
    </div>
  );
};
